/**
 * ambiguityToleranceEngine.js
 *
 * Determines whether Kevia should hold ambiguity open or move toward
 * resolution in its response.
 *
 * High ambiguity tolerance = permission to leave things unresolved,
 * acknowledge complexity without offering an answer.
 *
 * Low ambiguity tolerance = the situation has enough clarity that
 * resolution, direction, or a next step is appropriate.
 *
 * This module prevents the AI from flattening genuinely complex
 * pastoral situations into premature answers.
 */

/**
 * @param {{
 *   complexity:  "simple"|"layered"|"delicate"|"deeply_complex",
 *   hasTension:  boolean,
 *   emotion:     object|null,
 *   riskLevel:   string,
 *   intent:      string,
 * }} params
 * @returns {{
 *   toleranceLevel: "low"|"medium"|"high",
 *   instruction:    string|null,
 * }}
 */
export function assessAmbiguityTolerance({
  complexity,
  hasTension = false,
  emotion,
  riskLevel  = "none",
  intent,
}) {
  const dom = emotion?.dominantEmotion ?? "none";

  // Deeply complex + tension → high tolerance (resist resolving prematurely)
  if (complexity === "deeply_complex" && hasTension) {
    return {
      toleranceLevel: "high",
      instruction:
        "Esta situación no tiene resolución fácil. No la simplifiques. " +
        "Puedes acompañar la complejidad sin dar una respuesta definitiva.",
    };
  }

  // Delicate with emotional weight → high tolerance
  if (
    complexity === "delicate" &&
    (dom === "grief" || dom === "shame" || dom === "spiritual_confusion")
  ) {
    return {
      toleranceLevel: "high",
      instruction:
        "No hay que resolver todo hoy. Sostener la pregunta puede ser más pastoral " +
        "que dar una respuesta apresurada.",
    };
  }

  // Tension present but manageable → medium
  if (hasTension || complexity === "layered") {
    return {
      toleranceLevel: "medium",
      instruction: null,
    };
  }

  // Planning / analysis / sermon contexts want clear direction → low tolerance
  if (
    intent === "planning" ||
    intent === "analysis" ||
    intent === "sermon" ||
    intent === "visit_plan"
  ) {
    return {
      toleranceLevel: "low",
      instruction: null,
    };
  }

  // Critical risk needs action not holding → low tolerance
  if (riskLevel === "critical" || riskLevel === "high") {
    return {
      toleranceLevel: "low",
      instruction: null,
    };
  }

  return { toleranceLevel: "medium", instruction: null };
}
